"use client";

import Link from "next/link";
import { AddToCartButton } from "@/components/AddToCartButton";
import { useUserLists } from "@/components/UserListsProvider";
import { formatPrice, getProductImage, getProductImageAlt } from "@/lib/catalog";
import { extractProductDiameter } from "@/lib/product-merchandising";

type ComparePageClientProps = {
  showRetailPurchase: boolean;
  showB2B: boolean;
};

function areDifferent(values: string[]) {
  const normalized = values.map((value) => value.trim().toLocaleLowerCase("ru-RU"));
  return new Set(normalized).size > 1;
}

function getAttributeValue(
  attributes: Array<{ name: string; value: string }>,
  name: string
) {
  return attributes.find((attribute) => attribute.name === name)?.value ?? "—";
}

export function ComparePageClient({
  showRetailPurchase,
  showB2B
}: ComparePageClientProps) {
  const { isReady, compareItems, removeCompare, clearCompare } = useUserLists();
  const compareGridClass =
    compareItems.length >= 3
      ? "compare-cards-grid compare-cards-grid--three"
      : compareItems.length === 2
        ? "compare-cards-grid compare-cards-grid--two"
        : "compare-cards-grid compare-cards-grid--one";

  function getTableMeta(product: (typeof compareItems)[number]) {
    const diameter = extractProductDiameter(product);
    if (diameter) {
      return `Диаметр ${diameter}`;
    }

    if (product.sku) {
      return `Код ${product.sku}`;
    }

    return "Открыть товар";
  }

  const baseRows = [
    { name: "Цена", values: compareItems.map((item) => formatPrice(item.price)) },
    { name: "Старая цена", values: compareItems.map((item) => (item.oldPrice > 0 ? formatPrice(item.oldPrice) : "—")) },
    { name: "Наличие", values: compareItems.map((item) => (item.stockQty > 0 ? "Есть в наличии" : "Под заказ")) },
    { name: "Артикул", values: compareItems.map((item) => item.sku ?? "—") },
    { name: "Диаметр", values: compareItems.map((item) => extractProductDiameter(item) ?? "—") }
  ];

  const attributeNameSet = new Set<string>();
  for (const item of compareItems) {
    for (const attribute of item.attributes) {
      attributeNameSet.add(attribute.name);
    }
  }
  const attributeNames = Array.from(attributeNameSet);

  const attributeRows = attributeNames.map((name) => ({
    name,
    values: compareItems.map((item) => getAttributeValue(item.attributes, name))
  }));

  const allRows = [...baseRows, ...attributeRows];
  const rowsToShow = allRows.filter((row) => areDifferent(row.values));

  return (
    <main className="section">
      <div className="container">
        <div className="breadcrumbs">
          <Link href="/">Главная</Link>
          <span>/</span>
          <span>Сравнение</span>
        </div>

        <section className="list-page-head">
          <div>
            <div className="eyebrow">Сравнение</div>
            <h1 className="section-title">Сравнение товаров</h1>
            <p className="muted section-note">
              Добавьте до 3 товаров и сразу увидите, чем они отличаются по цене, наличию и характеристикам.
            </p>
          </div>

          {compareItems.length > 0 ? (
            <button type="button" className="ghost-button" onClick={clearCompare}>
              Очистить сравнение
            </button>
          ) : null}
        </section>

        {!isReady ? (
          <div className="empty-state">
            <h3>Подготавливаем сравнение</h3>
            <p className="muted">Загружаем выбранные товары.</p>
          </div>
        ) : compareItems.length === 0 ? (
          <div className="empty-state">
            <h3>Сравнивать пока нечего</h3>
            <p className="muted">
              Нажмите на значок сравнения в карточке товара, и позиция появится здесь.
            </p>
            <div className="empty-state-actions">
              <Link className="button" href="/catalog">
                Выбрать товары
              </Link>
            </div>
          </div>
        ) : (
          <>
            <div className={compareGridClass}>
              {compareItems.map((product) => (
                <article className="compare-card" key={product.slug}>
                  <button
                    type="button"
                    className="compare-remove-button"
                    onClick={() => removeCompare(product.slug)}
                  >
                    Убрать
                  </button>
                  <Link href={`/product/${product.slug}`} className="compare-card-media">
                    <img src={getProductImage(product)} alt={getProductImageAlt(product)} loading="lazy" />
                  </Link>
                  <div className="compare-card-body">
                    <div className="compare-card-meta">
                      {extractProductDiameter(product) ? (
                        <span className="compare-card-chip">Диаметр {extractProductDiameter(product)}</span>
                      ) : null}
                      {product.sku ? <span className="compare-card-code">Код {product.sku}</span> : null}
                    </div>
                    <Link
                      href={`/product/${product.slug}`}
                      className="compare-card-title"
                      title={product.name}
                    >
                      {product.name}
                    </Link>
                    <div className="compare-card-price">{formatPrice(product.price)}</div>
                    <div className="compare-card-stock">
                      {product.stockQty > 0 ? "Есть в наличии" : "Под заказ"}
                    </div>
                    {showRetailPurchase ? (
                      <AddToCartButton
                        className="button compare-card-button"
                        item={{
                          id: product.id,
                          slug: product.slug,
                          sku: product.sku,
                          name: product.name,
                          image: getProductImage(product),
                          price: product.price,
                          oldPrice: product.oldPrice,
                          unit: product.unit
                        }}
                      />
                    ) : (
                      <Link
                        className="button compare-card-button"
                        href={showB2B ? `/b2b?product=${product.slug}` : `/product/${product.slug}`}
                      >
                        {showB2B ? "Запрос для юрлиц" : "Открыть товар"}
                      </Link>
                    )}
                  </div>
                </article>
              ))}
            </div>

            {compareItems.length < 2 ? (
              <div className="empty-state compare-note">
                <h3>Добавьте еще товары</h3>
                <p className="muted">
                  Для полноценного сравнения выберите как минимум 2 позиции из каталога.
                </p>
              </div>
            ) : (
              <section className="compare-table-card">
                <div className="section-head section-head-retail">
                  <div>
                    <h2 className="section-title section-title-small">Ключевые отличия</h2>
                    <p className="muted section-note">
                      Таблица показывает только те параметры, в которых выбранные товары отличаются друг от друга.
                    </p>
                  </div>
                </div>

                <div className="compare-table-wrap">
                  <table className="compare-table">
                    <thead>
                      <tr>
                        <th>Параметр</th>
                        {compareItems.map((product) => (
                          <th key={product.slug}>
                            <Link href={`/product/${product.slug}`} className="compare-table-product">
                              <span className="compare-table-product-name">{product.name}</span>
                              <span className="compare-table-product-meta">{getTableMeta(product)}</span>
                            </Link>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(rowsToShow.length ? rowsToShow : allRows).map((row) => (
                        <tr key={row.name}>
                          <th>{row.name}</th>
                          {row.values.map((value, index) => (
                            <td key={`${row.name}-${compareItems[index]?.slug ?? index}`}>{value}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}
          </>
        )}
      </div>
    </main>
  );
}
